import { h } from 'preact';
import { useState } from 'preact/hooks';
import classNames from 'classnames';
import { isNil as _isNil } from 'lodash';
import { StarRating, StarRatingProps } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import Translate from '../../../components/translate/translate';
import Modal from '../../../components/modal/modal';
import Button from '../../../components/button/button';
import { useTranslation } from '../../../hooks/use-translation';
import AttributeBox from './attribute-box/attribute-box';
import RatingFormFooter from './rating-form-footer/rating-form-footer';

interface Props {
	onSubmit: (postRatingArgs: PostRatingArgs) => void;
	onSkip: () => void;
	attributeList: AttributeListItem[];
	goodRatingThreshold: number;
	rateUsCustomTitle?: string;
	ratingNegativeTitle?: string;
	ratingPositiveTitle?: string;
	allowFreeTextComment?: boolean;
	className?: string;
	isUserAuthenticated: boolean;
	showGreenDeliveryTitle?: boolean;
}

interface AttributeListItem {
	id: number;
	value: string;
}

export interface PostRatingArgs {
	rating: number;
	attributes: Attributes;
	comment?: string;
}

export interface RatingAttribute {
	isChecked: boolean;
	value: string;
}

export type Attributes = Map<number, RatingAttribute>;

const getAttributes = attributesList =>
	attributesList.reduce((attributes, attribute) => {
		attributes.set(attribute.id, { isChecked: false, value: attribute.value });
		return attributes;
	}, new Map());

const RatingForm = ({
	onSubmit: clientOnSubmit,
	attributeList,
	onSkip,
	className,
	goodRatingThreshold,
	rateUsCustomTitle,
	ratingNegativeTitle,
	ratingPositiveTitle,
	allowFreeTextComment,
	isUserAuthenticated,
	showGreenDeliveryTitle
}: Props) => {
	const { t } = useTranslation();
	const [rating, setRating] = useState(null);
	const [attributes, setAttributes] = useState<Attributes>(getAttributes(attributeList));
	const [comment, setComment] = useState<string>('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const onRate = rate => {
		setRating(rate);
	};
	const onSubmit = () => {
		if (_isNil(rating)) {
			setIsModalOpen(true);
			return;
		}

		clientOnSubmit({
			rating,
			attributes,
			comment
		});
	};

	const starAriaTranslations = {
		describedById: 'rate-us',
		labelOne: t('RATING_STAR_ALT_ONE'),
		labelTwo: t('RATING_STAR_ALT_TWO')
	} as StarRatingProps['ariaTranslations'];

	return (
		<div className={classNames('rating-form', className)} data-testid="rating-from-container">
			<Modal isOpen={isModalOpen} bodyClassName="rating-from-modal">
				<div className="rating-from-modal-title">
					<Translate text={'RATING.SELECT_STARS_BEFORE_SUBMIT'} />
				</div>
				<Button className="filled-button" onClick={() => setIsModalOpen(false)}>
					<Translate text={'OK'} />
				</Button>
			</Modal>
			<div>
				<div className="header">
					<div className="title aria-focus" tabIndex={0}>
						<Translate text="RATING.WHAT_WAS_YOUR_EXPERIENCE" />
					</div>
				</div>
				{showGreenDeliveryTitle && (
					<div className="green-delivery-indication" data-testid="green-delivery-container">
						<Translate text="RATING.ORDER_DELIVERED_GREEN" />
						<BringgIcon className="green-delivery-icon" iconName={BringgFontIcons.LeafEco} />
					</div>
				)}
				<label className="sub-header" id={'rate-us'}>
					{rateUsCustomTitle || <Translate text="RATING.HOW_WAS_YOUR_DELIVERY" />}
				</label>
				<StarRating
					rating={rating}
					onRate={onRate}
					className="rating"
					ariaTranslations={starAriaTranslations}
				/>
				<AttributeBox
					attributes={attributes}
					goodRatingThreshold={goodRatingThreshold}
					rating={rating}
					setAttributes={setAttributes}
					ratingNegativeTitle={ratingNegativeTitle}
					ratingPositiveTitle={ratingPositiveTitle}
					allowFreeTextComment={allowFreeTextComment}
					setComment={setComment}
					comment={comment}
				/>
			</div>
			{!isUserAuthenticated && <RatingFormFooter onSubmit={onSubmit} onSkip={onSkip} />}
		</div>
	);
};

export default RatingForm;
